<template>
    <ToggleGroupRoot
        v-bind="field?.controlAttrs.value"
        :aria-labelledby="field?.labelId"
        :model-value="modelValue"
        required
        type="single"
        class="inline-block"
        :class="field && 'self-start'"
        @update:model-value="onSelected">
        <div class="flex items-start border shadow-xs rounded-lgr" :class="invalid && 'border-status-error'">
            <ToggleGroupItem
                v-for="option of options"
                :key="option.key?.toString()"
                :value="option.key"
                class="px-4 py-2 hover:bg-toggle-option-hover data-[state=on]:bg-toggle-option-selected flex items-center justify-center bg-control-bg text-toggle-text first:rounded-l-lgr last:rounded-r-lgr first:border-none border-l body-small"
                :class="invalid && 'border-status-error'">
                {{ option.label }}
            </ToggleGroupItem>
        </div>
    </ToggleGroupRoot>
</template>

<script setup lang="ts">
import { useFormField } from "~vue/components/form/formFieldContext";
import { type AcceptableValue, ToggleGroupItem, ToggleGroupRoot } from "reka-ui";
import { computed } from "vue";

type BaseSingleToggleGroupProps = {
    options: { key: AcceptableValue; label: string }[];
    modelValue: AcceptableValue;
};

defineProps<BaseSingleToggleGroupProps>();

// Inside a FormField the group is named by the field's label (a `for` cannot reach a group) and
// takes its id, describedby and invalid wiring; while invalid, the frame and the dividers between
// options take BaseInput's invalid border. An `id` or `aria-labelledby` passed as an attribute
// still wins, since fallthrough attrs override these bindings. FormField stacks its parts in a
// flex column, so the group opts out of stretching to keep its options at their natural width.
const field = useFormField();
const invalid = computed(() => field?.invalid.value ?? false);

const emit = defineEmits<{
    "update:modelValue": [payload: AcceptableValue];
}>();

const onSelected = (newKey: AcceptableValue) => {
    if (!newKey) {
        // If newKey is falsy, the user tried to unselect the current option, but we need exactly
        // one option to be selected at all times, so we ignore these events.  (This is also why we
        // don't simply let the v-model binding pass through automatically).
        return;
    }

    emit("update:modelValue", newKey);
};
</script>
